import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2 } from "lucide-react";
import { promoText, promoMatchLine, promoImage } from "../../lib/promo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BADGE = { Live: "bg-green-100 text-green-700", Scheduled: "bg-blue-100 text-blue-700", Off: "bg-slate-100 text-slate-500", Ended: "bg-red-50 text-red-500" };
const field = "w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-[#3b9dff] focus:outline-none";

const PromoRow = ({ promo, token, onSaved, onDeleted }) => {
  const [f, setF] = useState(promo);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const set = (k, v) => { setF({ ...f, [k]: v }); setMsg(""); };
  const tid = promo.name.toLowerCase().replace(/[^a-z0-9]+/g, "-") + (promo.name === "New promotion" ? `-${promo.id.slice(0, 6)}` : "");

  const save = async () => {
    setBusy(true);
    const body = { name: f.name, start: f.start, end: f.end, percent: f.percent === "" || f.percent == null ? null : Number(f.percent), match_beat: !!f.match_beat, covers_addons: !!f.covers_addons, active: !!f.active, banner_text: f.banner_text || "" };
    const r = await fetch(`${API}/admin/promotions/${f.id}`, { method: "PUT", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify(body) });
    const d = await r.json();
    setBusy(false);
    if (!r.ok) return setMsg(typeof d.detail === "string" ? d.detail : "Could not save");
    setF(d); setMsg("Saved"); onSaved(d);
  };
  const del = async () => {
    if (!window.confirm(`Delete ${f.name}?`)) return;
    await fetch(`${API}/admin/promotions/${f.id}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    onDeleted(f.id);
  };
  const preview = f.percent ? `${f.name} special: ${promoText({ ...f, percent: f.percent })} ${promoMatchLine(f)}` : "Set a discount % to see the banner preview.";

  return (
    <div data-testid={`promo-row-${tid}`} className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        {promoImage(f) && <img src={promoImage(f)} alt="" data-testid={`promo-${tid}-art`} className="w-20 h-12 rounded-md object-cover ring-1 ring-slate-200" />}
        <input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid={`promo-${tid}-name`} className={`${field} max-w-[240px] font-bold`} />
        <span data-testid={`promo-${tid}-status`} className={`px-2 py-1 rounded-full text-xs font-semibold ${BADGE[f.status] || BADGE.Off}`}>{f.status}</span>
        {promo.results && (
          <span data-testid={`promo-${tid}-results`} className="text-xs text-slate-600 bg-[#f5f8ff] border border-slate-200 rounded-full px-3 py-1">
            During this promotion: <strong data-testid={`promo-${tid}-quotes`}>{promo.results.quotes}</strong> quote{promo.results.quotes === 1 ? "" : "s"}, <strong data-testid={`promo-${tid}-bookings`}>{promo.results.bookings}</strong> booking{promo.results.bookings === 1 ? "" : "s"}
          </span>
        )}
        <label className="ml-auto flex items-center gap-2 text-sm font-semibold text-[#0a1f44]">
          <input type="checkbox" checked={!!f.active} onChange={(e) => set("active", e.target.checked)} data-testid={`promo-${tid}-active`} className="w-4 h-4 accent-[#3b9dff]" /> On
        </label>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <label className="text-xs text-slate-500">Start<input type="date" value={f.start} onChange={(e) => set("start", e.target.value)} data-testid={`promo-${tid}-start`} className={field} /></label>
        <label className="text-xs text-slate-500">End<input type="date" value={f.end} onChange={(e) => set("end", e.target.value)} data-testid={`promo-${tid}-end`} className={field} /></label>
        <label className="text-xs text-slate-500">Install discount %<input type="number" min="1" max="90" value={f.percent ?? ""} onChange={(e) => set("percent", e.target.value)} placeholder="Blank" data-testid={`promo-${tid}-percent`} className={field} /></label>
        <label className="flex items-end gap-2 pb-2 text-sm text-[#0a1f44]">
          <input type="checkbox" checked={!!f.match_beat} onChange={(e) => set("match_beat", e.target.checked)} data-testid={`promo-${tid}-match`} className="w-4 h-4 accent-[#3b9dff]" /> Match or beat written quotes
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm text-[#0a1f44]">
        <input type="checkbox" checked={!!f.covers_addons} onChange={(e) => set("covers_addons", e.target.checked)} data-testid={`promo-${tid}-addons`} className="w-4 h-4 accent-[#3b9dff]" />
        Also discount wall and concealment add-ons
        <span className="text-xs text-slate-400">{f.covers_addons ? "(discount covers the full TV quote)" : "(discount covers install and mount only)"}</span>
      </label>
      <textarea rows={2} value={f.banner_text || ""} onChange={(e) => set("banner_text", e.target.value)} data-testid={`promo-${tid}-text`}
        placeholder={`Leave blank to use: "${f.percent || "X"}% off equipment, mounts, and installs through [end date]."`} className={field} />
      <p className="text-xs text-slate-500" data-testid={`promo-${tid}-preview`}>Banner preview: {preview}</p>
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} data-testid={`promo-${tid}-save`} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white text-sm font-semibold disabled:opacity-60">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
        </button>
        <button onClick={del} data-testid={`promo-${tid}-delete`} className="text-slate-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
        {msg && <span data-testid={`promo-${tid}-msg`} className={`text-sm ${msg === "Saved" ? "text-green-600" : "text-red-600"}`}>{msg}</span>}
      </div>
    </div>
  );
};

export const AdminPromotions = ({ token }) => {
  const [list, setList] = useState(null);
  const load = () => fetch(`${API}/admin/promotions`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()).then(setList);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
    await fetch(`${API}/admin/promotions`, { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }, body: JSON.stringify({ name: "New promotion", start: today, end: today }) });
    load();
  };

  if (!list) return <p className="text-slate-500">Loading promotions...</p>;
  return (
    <div data-testid="admin-promotions" className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <p className="text-sm text-slate-600 flex-1">Only one promotion shows at a time: the one that is On, in its date range, and ends soonest. A promotion needs a discount % before it can be turned on. The discount applies to the install and mount price in the quote wizard, plus wall and concealment add-ons if you tick that box.</p>
        <button onClick={add} data-testid="promo-add-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#0a1f44] text-white text-sm font-semibold"><Plus className="w-4 h-4" /> New promotion</button>
      </div>
      {list.map((p) => (
        <PromoRow key={p.id} promo={p} token={token} onSaved={() => load()} onDeleted={(id) => setList(list.filter((x) => x.id !== id))} />
      ))}
    </div>
  );
};
